import React from 'react';
import { useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { ArrowLeftIcon } from '@rsos/assets/icons';
import { Box, Text, Button } from '@rsos/base-components';
import Select from '@rsos/base-components/Select';
import InputText from '@rsos/base-components/form/inputs/InputText';
import { setSelectedTool } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import styled, { type Theme } from '@rsos/theme/styled';
import {
  useFormSubmit,
  DISCREPANCY_TYPE_OPTIONS,
  DISCREPANCY_TYPE_DEFAULT,
  type AliDiscrepancyFormData,
} from '../useFormSubmit';
import Alert from './Alert';

/**
 * Schema for validating tool properties passed to ALI Discrepancy form
 */
export const aliDiscrepancyToolPropertiesSchema = yup.object().shape({
  phoneNumber: yup.string(),
  originalAddress: yup.string().optional(),
  classOfService: yup.string().optional(),
  esn: yup.string().optional(),
  community: yup.string().optional(),
  rawAli: yup.string().optional(),
});

/**
 * Type of tool properties passed to ALI Discrepancy form
 */
export type AliDiscrepancyToolProperties = yup.InferType<
  typeof aliDiscrepancyToolPropertiesSchema
>;

const Header = styled.div`
  display: flex;
  align-items: start;
  margin-bottom: 24px;
  gap: 8px;
`;

const FormSection = styled(Box)`
  margin-bottom: 24px;
`;

const SectionTitle = styled(Text).attrs({
  size: 'normal',
  fontWeight: 'bold',
  color: 'primaryText',
})`
  margin-bottom: 16px;
  display: block;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
`;

const ButtonContainer = styled(Box)`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 32px;
`;

const Label = styled(Text).attrs({
  size: 'small',
  color: 'secondaryText',
})`
  display: block;
  margin-bottom: 4px;
`;

const TextArea = styled.textarea<{ theme: Theme }>`
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  font-family: inherit;
  border: 1px solid ${({ theme }) => theme.colors.heavyLineOutline};
  border-radius: 4px;
  resize: vertical;
  min-height: 100px;
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.primaryText};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.heavyDanger};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.secondaryText};
  }
`;

/**
 * Extracts initial form values from tool properties
 * @param toolProperties - Properties passed when the tool was invoked
 * @returns Partial form data with pre-populated original fields
 */
function extractFormValuesFromToolProperties(
  toolProperties: unknown,
): Partial<AliDiscrepancyFormData> {
  // Return empty object if no properties provided
  if (!toolProperties || typeof toolProperties !== 'object') {
    return {};
  }

  const props = aliDiscrepancyToolPropertiesSchema.validateSync(
    toolProperties,
    {
      stripUnknown: true,
      abortEarly: false,
    },
  );

  const initialValues: Partial<AliDiscrepancyFormData> = {};

  // Map original address if available
  if (props.originalAddress) {
    initialValues.original_address = props.originalAddress;
  }

  // Map class of service to call_type if available
  if (props.classOfService) {
    initialValues.call_type = props.classOfService;
  }

  // Map phone number if available
  if (props.phoneNumber) {
    initialValues.original_telephone_number = props.phoneNumber;
  }

  // Map ESN if available
  if (props.esn) {
    initialValues.original_esn = props.esn;
  }

  // Map community if available
  if (props.community) {
    initialValues.original_community = props.community;
  }

  // Map raw ALI for roundtrip (not displayed in form)
  if (props.rawAli) {
    initialValues.raw_ali = props.rawAli;
  }

  return initialValues;
}

// Build initial values from tool properties
function getInitialFormValues(toolProperties: unknown): AliDiscrepancyFormData {
  const defaultValues: AliDiscrepancyFormData = {
    discrepancy_type: DISCREPANCY_TYPE_DEFAULT.value,
    call_type: '',
    original_telephone_number: '',
    original_name: '',
    original_address: '',
    original_community: '',
    original_esn: '',
    corrected_telephone_number: '',
    corrected_name: '',
    corrected_address: '',
    corrected_community: '',
    corrected_esn: '',
    comments: '',
    raw_ali: '',
  };

  // Extract and merge values from toolProperties
  const extractedValues = extractFormValuesFromToolProperties(toolProperties);

  return {
    ...defaultValues,
    ...extractedValues,
  };
}

const validateForm = (values: AliDiscrepancyFormData) => {
  const errors: Partial<Record<keyof AliDiscrepancyFormData, string>> = {};

  if (
    !values.original_telephone_number ||
    values.original_telephone_number.trim() === ''
  ) {
    errors.original_telephone_number = 'Required';
  }

  if (!values.original_address || values.original_address.trim() === '') {
    errors.original_address = 'Required';
  }

  return errors;
};

interface AliDiscrepancyFormProps {
  /**
   * Properties passed when the tool was invoked. Must be validated by @see aliDiscrepancyToolPropertiesSchema and conform to @see AliDiscrepancyToolProperties.
   * Typed as @see unknown to be compatible with eventPaneSlice's setSelectedTool action/state.
   */
  toolProperties?: unknown;
}

export const AliDiscrepancyForm: React.FC<AliDiscrepancyFormProps> = ({
  toolProperties,
}) => {
  const { submitDiscrepancy, error, success } = useFormSubmit();
  const dispatch = useDispatch();

  const handleCancel = () => {
    dispatch(setSelectedTool(null));
  };

  const handleSubmit = async (values: AliDiscrepancyFormData) => {
    await submitDiscrepancy(values);
  };

  return (
    <div data-name="ali-discrepancy-form">
      <Header>
        <Button size="xxsmall" onClick={handleCancel} btnType="tertiary">
          <ArrowLeftIcon width={12} />
        </Button>
        <div>
          <Text size="large" fontWeight="bold" color="primaryText">
            Report Discrepancy
          </Text>
          <Text size="small" color="secondaryText">
            INdigital
          </Text>
        </div>
      </Header>

      {error && <Alert variant="error">{error}</Alert>}

      {success && (
        <Alert variant="success">Report was successfully submitted</Alert>
      )}

      <Formik
        initialValues={getInitialFormValues(toolProperties)}
        validate={validateForm}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
        }) => (
          <Form>
            <FormSection>
              <SectionTitle>Discrepancy Details</SectionTitle>

              <Box pb={3}>
                <Label>Discrepancy Type</Label>
                <Select
                  name="discrepancy_type"
                  defaultValue={DISCREPANCY_TYPE_DEFAULT}
                  options={DISCREPANCY_TYPE_OPTIONS}
                  onChange={(option: { value: string; label: string }) => {
                    handleChange({
                      target: {
                        name: 'discrepancy_type',
                        value: option?.value || '',
                      },
                    });
                  }}
                  onBlur={handleBlur}
                  isClearable={false}
                />
              </Box>

              <Box pb={3}>
                <Label>Call Type</Label>
                <InputText
                  name="call_type"
                  value={values.call_type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="e.g., BUSN, RES, WPH1"
                />
              </Box>
            </FormSection>

            <FormSection>
              <SectionTitle>Corrected Call Data</SectionTitle>

              <FormRow>
                <Box>
                  <Label>Original Telephone Number *</Label>
                  <InputText
                    name="original_telephone_number"
                    value={values.original_telephone_number}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isDisabled
                    error={
                      touched.original_telephone_number &&
                      errors.original_telephone_number
                        ? errors.original_telephone_number
                        : null
                    }
                  />
                </Box>
                <Box>
                  <Label>Corrected Telephone Number</Label>
                  <InputText
                    name="corrected_telephone_number"
                    value={values.corrected_telephone_number}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Box>
              </FormRow>

              <FormRow>
                <Box>
                  <Label>Original Name</Label>
                  <InputText
                    name="original_name"
                    value={values.original_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isDisabled
                  />
                </Box>
                <Box>
                  <Label>Corrected Name</Label>
                  <InputText
                    name="corrected_name"
                    value={values.corrected_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Box>
              </FormRow>

              <FormRow>
                <Box>
                  <Label>Original Address *</Label>
                  <InputText
                    name="original_address"
                    value={values.original_address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isDisabled
                    error={
                      touched.original_address && errors.original_address
                        ? errors.original_address
                        : null
                    }
                  />
                </Box>
                <Box>
                  <Label>Corrected Address</Label>
                  <InputText
                    name="corrected_address"
                    value={values.corrected_address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Box>
              </FormRow>

              <FormRow>
                <Box>
                  <Label>Original Community</Label>
                  <InputText
                    name="original_community"
                    value={values.original_community}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isDisabled
                  />
                </Box>
                <Box>
                  <Label>Corrected Community</Label>
                  <InputText
                    name="corrected_community"
                    value={values.corrected_community}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Box>
              </FormRow>

              <FormRow>
                <Box>
                  <Label>Original ESN</Label>
                  <InputText
                    name="original_esn"
                    value={values.original_esn}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isDisabled
                  />
                </Box>
                <Box>
                  <Label>Corrected ESN</Label>
                  <InputText
                    name="corrected_esn"
                    value={values.corrected_esn}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Box>
              </FormRow>
            </FormSection>

            <FormSection>
              <SectionTitle>Comments</SectionTitle>
              <TextArea
                name="comments"
                placeholder="Type here"
                value={values.comments}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormSection>

            <ButtonContainer>
              <Button
                btnType="tertiary"
                type="button"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                btnType="primary"
                type="submit"
                disabled={isSubmitting || Object.keys(errors).length > 0}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Form'}
              </Button>
            </ButtonContainer>
          </Form>
        )}
      </Formik>
    </div>
  );
};
